class AnalyticsService {
  constructor() {
    // Google Analytics 4
    this.gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    // Яндекс.Метрика: из env или подставляется из админки (public-settings)
    this.ymId = process.env.REACT_APP_YANDEX_METRIKA_ID || null;
    this.ymLoadedFromSettings = false;
    this.isEnabled = this.checkConsent();
    this.isInitialized = false;
    this.gaScriptLoaded = false;
  }

  checkConsent() {
    // Проверяем согласие пользователя
    const consent = localStorage.getItem('analytics_consent');
    return consent === 'true';
  }

  // Загрузка Google Tag (gtag.js)
  loadGoogleTag() {
    if (this.gaScriptLoaded || !this.gaId) return;

    // Проверяем, не загружен ли уже скрипт из index.html
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${this.gaId}"]`);
    if (existingScript) {
      this.gaScriptLoaded = true;
      // Скрипт уже загружен из index.html, инициализируем конфигурацию
      // Проверяем, не инициализирован ли уже GA
      if (window.gtag && window.dataLayer) {
        // Проверяем, была ли уже применена конфигурация
        // Если dataLayer пустой или нет событий конфигурации, инициализируем
        const hasConfig = window.dataLayer.some(item => 
          item && item[0] === 'config' && item[1] === this.gaId
        );
        
        if (!hasConfig) {
          window.gtag('config', this.gaId, {
            anonymize_ip: true
          });
        }
      }
      return;
    }

    // Fallback: если скрипт не загружен из index.html (не должно происходить)
    // Создаем dataLayer, если еще не создан
    if (!window.dataLayer) {
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
    }

    // Загружаем скрипт динамически (fallback)
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
    document.head.appendChild(script1);

    // Инициализируем конфигурацию после загрузки скрипта
    script1.onload = () => {
      if (window.gtag) {
        window.gtag('config', this.gaId, {
          anonymize_ip: true
        });
      }
    };

    this.gaScriptLoaded = true;
  }

  init() {
    if (!this.isEnabled) return;

    // Инициализация GA4 через Google Tag
    if (this.gaId && typeof window !== 'undefined') {
      this.loadGoogleTag();
    }

    // Инициализация Яндекс.Метрики
    if (this.ymId && typeof window !== 'undefined') {
      // Проверяем, не загружен ли уже скрипт из index.html
      const existingScript = document.querySelector('script[src*="mc.yandex.ru/metrika/tag.js"]');
      if (existingScript) {
        // Скрипт уже загружен из index.html, просто инициализируем если еще не инициализировано
        if (window.ym) {
          window.ym(this.ymId, "init", {
            ssr: true,
            webvisor: true,
            clickmap: true,
            ecommerce: "dataLayer",
            accurateTrackBounce: true,
            trackLinks: true,
            anonymize_ip: true
          });
        }
        return;
      }

      // Загружаем скрипт динамически (если не загружен из index.html)
      // eslint-disable-next-line no-unused-expressions
      (function(m,e,t,r,i,k,a){
        m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments);};
        m[i].l=1*new Date();
        // Проверка на дублирование скрипта (как в официальной инструкции Яндекс)
        for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
        k=e.createElement(t);
        a=e.getElementsByTagName(t)[0];
        k.async=1;
        k.src=r;
        a.parentNode.insertBefore(k,a);
      })(window, document, "script", `https://mc.yandex.ru/metrika/tag.js?id=${this.ymId}`, "ym");

      if (window.ym) {
        window.ym(this.ymId, "init", {
          ssr: true,
          webvisor: true,
          clickmap: true,
          ecommerce: "dataLayer",
          accurateTrackBounce: true,
          trackLinks: true,
          anonymize_ip: true
        });
      }
    }

    this.isInitialized = true;
  }

  /**
   * Загружает настройки Яндекс.Метрики из админки (БД).
   * Если REACT_APP_YANDEX_METRIKA_ID не задан, но в настройках включено и указан ID — подключаем счётчик
   * (в т.ч. для обхода страниц роботом, без проверки согласия на куки).
   */
  async initYandexFromSettings() {
    if (this.ymId != null && this.ymId !== '') return;
    try {
      const { getPublicSettings } = await import('../utils/api');
      const res = await getPublicSettings();
      const data = res.data || {};
      const enabled = data.yandex_metrika_enabled === 'true' || data.yandex_metrika_enabled === '1';
      const counterId = (data.yandex_metrika_counter_id || '').trim();
      if (!enabled || !counterId) return;
      const id = counterId.replace(/\D/g, '');
      if (!id) return;
      if (document.querySelector('script[src*="mc.yandex.ru/metrika/tag.js"]')) return;
      this.ymId = id;
      this.ymLoadedFromSettings = true;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://mc.yandex.ru/metrika/tag.js?id=${id}`;
      script.onload = () => {
        if (typeof window.ym === 'function') {
          window.ym(id, 'init', {
            ssr: true,
            webvisor: true,
            clickmap: true,
            ecommerce: 'dataLayer',
            accurateTrackBounce: true,
            trackLinks: true,
            referrer: document.referrer,
            url: window.location.href,
          });
        }
      };
      const first = document.getElementsByTagName('script')[0];
      if (first?.parentNode) first.parentNode.insertBefore(script, first);
      else document.head.appendChild(script);
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.src = `https://mc.yandex.ru/watch/${id}`;
      img.style.cssText = 'position:absolute;left:-9999px;';
      img.alt = '';
      noscript.appendChild(img);
      document.body.appendChild(noscript);
    } catch (_) {
      // ignore
    }
  }

  // Универсальные события (отправка во все активные сервисы)
  trackEvent(eventName, parameters = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    // GA4 события через gtag
    if (this.gaId && window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        event_category: parameters.category || 'general',
        event_label: parameters.label,
        value: parameters.value
      });
    }

    // Яндекс.Метрика события
    if (this.ymId && window.ym) {
      window.ym(this.ymId, 'reachGoal', eventName, parameters);
    }
  }

  // Отслеживание страниц
  trackPageView(path, title) {
    if (!this.isEnabled || !this.isInitialized) return;

    // GA4 через gtag
    if (this.gaId && window.gtag) {
      window.gtag('config', this.gaId, {
        page_path: path,
        page_title: title,
        anonymize_ip: true
      });
    }

    // Яндекс.Метрика
    if (this.ymId && window.ym) {
      window.ym(this.ymId, 'hit', path, { title });
    }
  }

  // Установка пользователя
  setUserId(userId) {
    if (!this.isEnabled || !this.isInitialized) return;

    // GA4 через gtag
    if (this.gaId && window.gtag) {
      window.gtag('config', this.gaId, {
        user_id: userId,
        anonymize_ip: true
      });
    }

    // Яндекс.Метрика
    if (this.ymId && window.ym) {
      window.ym(this.ymId, 'setUserID', userId);
    }
  }

  // Пользовательские параметры
  setUserProperties(properties) {
    if (!this.isEnabled || !this.isInitialized) return;

    // GA4 через gtag
    if (this.gaId && window.gtag) {
      window.gtag('set', 'user_properties', properties);
    }
  }

  // Включение/отключение
  enable() {
    localStorage.setItem('analytics_consent', 'true');
    this.isEnabled = true;
    
    // Если скрипт уже загружен из index.html, но конфигурация не применена
    // (пользователь дал согласие после загрузки страницы)
    if (window.gtag && window.dataLayer && this.gaId) {
      // Проверяем, была ли уже применена конфигурация
      const hasConfig = window.dataLayer.some(item => 
        item && item[0] === 'config' && item[1] === this.gaId
      );
      
      if (!hasConfig) {
        // Инициализируем GA сразу после согласия
        window.gtag('config', this.gaId, {
          anonymize_ip: true
        });
        this.gaScriptLoaded = true;
      }
    }
    
    if (!this.isInitialized) {
      this.init();
    }
  }

  disable() {
    localStorage.setItem('analytics_consent', 'false');
    this.isEnabled = false;
    // Очистка данных (опционально)
    if (this.gaId && window.gtag) {
      // Отключаем отслеживание
      window.gtag('config', this.gaId, {
        'anonymize_ip': true,
        'cookie_flags': 'SameSite=None;Secure'
      });
    }
  }
}

export default new AnalyticsService();

